import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useId } from "react";

import icDrag from "@/assets/images/console/icDrag.svg";
import { BoardItem } from "@/pages/console/board/BoardList";

interface DraggableTableProps<T> {
    items: T[];
    renderRow: (item: T, index: number) => React.ReactNode;
    getItemId: (item: T, index: number) => string | number;
    thead: React.ReactNode;
    handleOrderChange: (idx: number, moveNum: number) => void;
}

export default function DraggableTable<T>({
    items,
    renderRow,
    getItemId,
    thead,
    handleOrderChange,
}: DraggableTableProps<T>) {
    // DndContext 에 고유id 추가 (ssr에러 해결)
    const id = useId();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const overItem = items.find((item, i) => getItemId(item, i) === over.id);
        const moveNum = (overItem as BoardItem).b_num;
        handleOrderChange(Number(active.id), moveNum);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            id={id}
            modifiers={[restrictToParentElement]}
        >
            <table>
                {thead}
                <tbody>
                    <SortableContext items={items.map(getItemId)} strategy={verticalListSortingStrategy}>
                        {items.map((item, i) => (
                            <DraggableRow key={getItemId(item, i)} id={getItemId(item, i)}>
                                {renderRow(item, i)}
                            </DraggableRow>
                        ))}
                    </SortableContext>
                </tbody>
            </table>
        </DndContext>
    );
}

function DraggableRow({ id, children }: { id: string | number; children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className="border-b border-[#ddd] text-center text-[#666] hover:bg-[#f7f6fb]"
        >
            {children}
            <td className="p-3">
                <button type="button" className="w-full cursor-grab" {...attributes} {...listeners}>
                    <img src={icDrag} alt="순서변경" className="mx-auto" />
                </button>
            </td>
        </tr>
    );
}
